// PRODUCTION: Les données sont récupérées depuis IndexedDB au lieu des mocks
import { IndexedDBService } from './storage/IndexedDBService';
import type { Message, Conversation } from '../types/chat';

// Valeurs par défaut pour l'utilisateur (seront remplacées par les données réelles de l'auth)
const getDefaultUser = () => {
  // Récupérer les infos utilisateur depuis localStorage si disponibles
  const storedUser = localStorage.getItem('wanzo_user');
  if (storedUser) {
    try {
      const parsed = JSON.parse(storedUser);
      return {
        id: parsed.id || 'unknown',
        name: parsed.name || parsed.fullName || 'Utilisateur',
        email: parsed.email || '',
        role: parsed.role || 'Utilisateur',
        department: parsed.department || 'Non spécifié',
        permissions: parsed.permissions || ['read'],
        lastLogin: parsed.lastLogin || new Date().toISOString()
      };
    } catch {
      // Fallback si parsing échoue
    }
  }
  return {
    id: 'unknown',
    name: 'Utilisateur',
    email: '',
    role: 'Utilisateur',
    department: 'Non spécifié',
    permissions: ['read'],
    lastLogin: new Date().toISOString()
  };
};

// Valeurs par défaut pour l'organisation (seront remplacées par les données réelles)
const getDefaultOrganization = () => {
  // Récupérer les infos organisation depuis localStorage si disponibles
  const storedOrg = localStorage.getItem('wanzo_organization');
  if (storedOrg) {
    try {
      const parsed = JSON.parse(storedOrg);
      return {
        name: parsed.name || 'Organisation',
        type: parsed.type || 'Non spécifié',
        sector: parsed.sector || 'Non spécifié',
        address: parsed.address || '',
        taxNumber: parsed.taxNumber || parsed.nif || '',
        registrationNumber: parsed.registrationNumber || parsed.rccm || '',
        currency: parsed.currency || 'CDF',
        fiscalYear: parsed.fiscalYear || {
          start: `${new Date().getFullYear()}-01-01`,
          end: `${new Date().getFullYear()}-12-31`,
          current: `${new Date().getFullYear()}`
        },
        employees: parsed.employees || 0,
        capital: parsed.capital || 0,
        activity: parsed.activity || parsed.description || ''
      };
    } catch {
      // Fallback si parsing échoue
    }
  }
  const currentYear = new Date().getFullYear();
  return {
    name: 'Organisation',
    type: 'Non spécifié',
    sector: 'Non spécifié',
    address: '',
    taxNumber: '',
    registrationNumber: '',
    currency: 'CDF',
    fiscalYear: {
      start: `${currentYear}-01-01`,
      end: `${currentYear}-12-31`,
      current: `${currentYear}`
    },
    employees: 0,
    capital: 0,
    activity: ''
  };
};

// Bilan vide par défaut
const getEmptyBalanceSheet = () => ({
  fixedAssets: { total: { net: 0 } },
  currentAssets: { total: { net: 0 } },
  equity: { capital: 0 },
  liabilities: { total: 0 }
});

export interface BusinessContext {
  user: ReturnType<typeof getDefaultUser>;
  organization: ReturnType<typeof getDefaultOrganization>;
  journalEntries: any[];
  accounts: any[];
  balanceSheet: any;
  declarations: any[];
  ledgerEntries: any[];
  recentTransactions: any[];
}

export interface ConversationContext {
  messageHistory: string[];
  businessContext: BusinessContext;
  userProfile: {
    role: string;
    permissions: string[];
    preferences: {
      currency: string;
      language: string;
      dateFormat: string;
    };
  };
  sessionContext: {
    currentPeriod: string;
    fiscalYear: string;
    organizationInfo: {
      name: string;
      type: string;
      sector: string;
    };
  };
}

// Cache pour les données chargées depuis IndexedDB
let cachedBusinessData: {
  journalEntries: any[];
  accounts: any[];
  ledgerEntries: any[];
  declarations: any[];
  lastUpdated: number;
} | null = null;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export class ContextService {
  
  /**
   * Charge les données depuis IndexedDB de manière asynchrone
   */
  static async loadDataFromIndexedDB(): Promise<void> {
    const now = Date.now();
    
    // Utiliser le cache si encore valide
    if (cachedBusinessData && (now - cachedBusinessData.lastUpdated) < CACHE_DURATION) {
      return;
    }

    try {
      const [journalEntries, accounts, declarations] = await Promise.all([
        IndexedDBService.getAll('journal_entries').catch(() => []),
        IndexedDBService.getAll('accounts').catch(() => []),
        IndexedDBService.getAll('declarations').catch(() => [])
      ]);

      cachedBusinessData = {
        journalEntries: Array.isArray(journalEntries) ? journalEntries : [],
        accounts: Array.isArray(accounts) ? accounts : [],
        ledgerEntries: [], // Le grand livre est dérivé des écritures
        declarations: Array.isArray(declarations) ? declarations : [],
        lastUpdated: now
      };
    } catch (error) {
      console.warn('[ContextService] Erreur lors du chargement IndexedDB:', error);
      // Utiliser des tableaux vides en cas d'erreur
      cachedBusinessData = {
        journalEntries: [],
        accounts: [],
        ledgerEntries: [],
        declarations: [],
        lastUpdated: now
      };
    }
  }

  /**
   * Génère le contexte métier avec les données réelles depuis IndexedDB/localStorage
   * Note: Appeler loadDataFromIndexedDB() avant pour charger les données fraîches
   */
  static getBusinessContext(): BusinessContext {
    const data = cachedBusinessData || {
      journalEntries: [],
      accounts: [],
      ledgerEntries: [],
      declarations: []
    };

    return {
      user: getDefaultUser(),
      organization: getDefaultOrganization(),
      journalEntries: data.journalEntries.slice(0, 10), // 10 dernières écritures
      accounts: data.accounts.slice(0, 20), // 20 comptes principaux
      balanceSheet: getEmptyBalanceSheet(),
      declarations: data.declarations.slice(0, 5),
      ledgerEntries: data.ledgerEntries.slice(0, 15), // 15 dernières écritures
      recentTransactions: data.journalEntries
        .filter((entry: any) => new Date(entry.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
        .slice(0, 5) // Transactions des 30 derniers jours
    };
  }

  /**
   * Version asynchrone qui charge les données fraîches avant de retourner le contexte
   */
  static async getBusinessContextAsync(): Promise<BusinessContext> {
    await this.loadDataFromIndexedDB();
    return this.getBusinessContext();
  }

  /**
   * Invalide le cache pour forcer un rechargement
   */
  static invalidateCache(): void {
    cachedBusinessData = null;
  }

  /**
   * Analyse le contenu des messages pour déterminer le contexte nécessaire
   */
  static analyzeContextNeeds(messages: Message[]): {
    needsJournal: boolean;
    needsAccounts: boolean;
    needsFinancialData: boolean;
    needsDeclarations: boolean;
    needsAnalytics: boolean;
  } {
    const allContent = messages.map(m => m.content.toLowerCase()).join(' ');
    
    return {
      needsJournal: /écriture|journal|comptabiliser|passation/.test(allContent),
      needsAccounts: /compte|plan comptable|balance|grand livre/.test(allContent),
      needsFinancialData: /bilan|compte de résultat|états financiers|ratio/.test(allContent),
      needsDeclarations: /déclaration|tva|impôt|fiscal|taxe/.test(allContent),
      needsAnalytics: /analyse|tendance|évolution|performance|kpi/.test(allContent)
    };
  }

  /**
   * Construit le contexte enrichi pour OpenAI
   */
  static buildOpenAIContext(conversation: Conversation): any[] {
    const contextNeeds = this.analyzeContextNeeds(conversation.messages);
    const businessContext = this.getBusinessContext();
    
    const context: any[] = [];

    // Message système avec le rôle et les données de base
    context.push({
      role: 'system',
      content: `Tu es Adha, un assistant IA spécialisé en comptabilité pour la République Démocratique du Congo.

PROFIL UTILISATEUR ACTUEL:
- Nom: ${businessContext.user.name}
- Rôle: ${businessContext.user.role}
- Département: ${businessContext.user.department}
- Email: ${businessContext.user.email}

INFORMATIONS ORGANISATION:
- Nom: ${businessContext.organization.name}
- Type: ${businessContext.organization.type}
- Secteur: ${businessContext.organization.sector}
- Adresse: ${businessContext.organization.address}
- N° Fiscal: ${businessContext.organization.taxNumber}
- Capital: ${this.formatCurrency(businessContext.organization.capital)}
- Employés: ${businessContext.organization.employees}
- Activité: ${businessContext.organization.activity}
- Devise: ${businessContext.organization.currency}
- Exercice fiscal: ${businessContext.organization.fiscalYear.current}

DONNÉES DISPONIBLES:
${contextNeeds.needsAccounts ? `- Plan comptable avec ${businessContext.accounts.length} comptes actifs` : ''}
${contextNeeds.needsJournal ? `- ${businessContext.journalEntries.length} écritures comptables récentes` : ''}
${contextNeeds.needsFinancialData ? '- États financiers (Bilan, Compte de résultat)' : ''}
${contextNeeds.needsDeclarations ? `- ${businessContext.declarations.length} déclarations en cours` : ''}

INSTRUCTIONS DE FORMATAGE IMPORTANTES:
1. **Répondre TOUJOURS en français** avec un ton professionnel et bienveillant
2. **Utiliser le markdown** pour structurer tes réponses:
   - **Gras** pour les titres et points importants
   - *Italique* pour les termes techniques
   - \`Code\` pour les références de comptes
   - Tables markdown pour les données tabulaires
   - Listes à puces et numérotées pour organiser l'information
   - Blockquotes (>) pour les citations réglementaires
3. **Formatage des montants**: Toujours en CDF avec séparateurs de milliers (ex: 1.250.000 CDF)
4. **Utiliser LaTeX** pour les formules comptables complexes: 
   - Inline: $formule$ 
   - Block: $$formule$$
5. **Créer des tableaux** pour présenter les données financières de manière claire
6. **Proposer des analyses visuelles** avec des suggestions de graphiques quand pertinent
7. **Utiliser les données contextuelles** fournies ci-dessous pour donner des réponses précises
8. **Citer les articles réglementaires** RDC quand applicable
9. **Suggérer des actions concrètes** et des prochaines étapes

Tu as accès aux données réelles de ${businessContext.organization.name} ci-dessous.`
    });

    // Ajouter les données métier selon les besoins
    if (contextNeeds.needsAccounts) {
      context.push({
        role: 'system',
        content: `PLAN COMPTABLE (Extrait):
${businessContext.accounts.slice(0, 10).map(acc => 
  `- ${acc.code} - ${acc.name}: ${acc.balance?.toLocaleString('fr-FR')} CDF`
).join('\n')}`
      });
    }

    if (contextNeeds.needsJournal) {
      context.push({
        role: 'system',
        content: `ÉCRITURES RÉCENTES:
${businessContext.journalEntries.slice(0, 5).map(entry => 
  `- ${entry.date}: ${entry.description} - ${entry.totalDebit?.toLocaleString('fr-FR')} CDF`
).join('\n')}`
      });
    }

    if (contextNeeds.needsFinancialData) {
      context.push({
        role: 'system',
        content: `BILAN (Extrait):
ACTIF:
- Total Actif immobilisé: ${businessContext.balanceSheet.fixedAssets.total.net?.toLocaleString('fr-FR')} CDF
- Total Actif circulant: ${businessContext.balanceSheet.currentAssets.total.net?.toLocaleString('fr-FR')} CDF

PASSIF:
- Capitaux propres: ${businessContext.balanceSheet.equity?.capital?.toLocaleString('fr-FR')} CDF`
      });
    }

    if (contextNeeds.needsDeclarations) {
      context.push({
        role: 'system',
        content: `DÉCLARATIONS:
${businessContext.declarations.map(decl => 
  `- ${decl.type}: ${decl.amount?.toLocaleString('fr-FR')} CDF - Statut: ${decl.status}`
).join('\n')}`
      });
    }

    // Ajouter l'historique des messages (derniers 8 messages)
    const recentMessages = conversation.messages.slice(-8);
    recentMessages.forEach(message => {
      context.push({
        role: message.sender === 'user' ? 'user' : 'assistant',
        content: message.content
      });
    });

    return context;
  }

  /**
   * Construit le contexte pour le backend
   */
  static buildBackendContext(conversation: Conversation): {
    messages: any[];
    businessData: BusinessContext;
    metadata: any;
  } {
    return {
      messages: conversation.messages.slice(-10).map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.content,
        timestamp: msg.timestamp
      })),
      businessData: this.getBusinessContext(),
      metadata: {
        organizationId: 'wanzo-sarl',
        fiscalYear: new Date().getFullYear(),
        currency: 'CDF',
        contextNeeds: this.analyzeContextNeeds(conversation.messages)
      }
    };
  }

  /**
   * Construit le contexte pour les réponses mockées
   */
  static buildMockContext(conversation: Conversation): {
    lastMessages: string[];
    businessSummary: string;
    contextHints: string[];
  } {
    const businessContext = this.getBusinessContext();
    const contextNeeds = this.analyzeContextNeeds(conversation.messages);
    
    const contextHints: string[] = [];
    
    if (contextNeeds.needsJournal) {
      contextHints.push(`Dernière écriture: ${businessContext.journalEntries[0]?.description}`);
    }
    
    if (contextNeeds.needsAccounts) {
      contextHints.push(`Plan comptable disponible avec ${businessContext.accounts.length} comptes`);
    }
    
    if (contextNeeds.needsFinancialData) {
      contextHints.push('États financiers à jour pour l\'exercice en cours');
    }
    
    if (contextNeeds.needsDeclarations) {
      contextHints.push(`${businessContext.declarations.length} déclarations en cours de traitement`);
    }

    return {
      lastMessages: conversation.messages.slice(-5).map(msg => msg.content),
      businessSummary: `WANZO SARL - ${businessContext.journalEntries.length} écritures, ${businessContext.accounts.length} comptes actifs`,
      contextHints
    };
  }

  /**
   * Formate les montants en CDF avec séparateurs
   */
  static formatCurrency(amount: number): string {
    return `${amount.toLocaleString('fr-FR')} CDF`;
  }

  /**
   * Génère un résumé du contexte pour les logs
   */
  static getContextSummary(conversation: Conversation): string {
    const contextNeeds = this.analyzeContextNeeds(conversation.messages);
    const businessContext = this.getBusinessContext();
    
    return `Contexte: ${Object.entries(contextNeeds).filter(([_, needed]) => needed).map(([key]) => key).join(', ')} | ${businessContext.journalEntries.length} écritures | ${businessContext.accounts.length} comptes`;
  }
}
